// Ensure webpack chunk loading points to the correct asset base URL
// This must run BEFORE any webpack chunks are loaded
// We must access the runtime public path via the global object to avoid
// ReferenceError in ES module / strict mode where assigning to an
// undeclared identifier throws. Use (globalThis as any).__webpack_public_path__
// so it safely sets the global property.
declare let __webpack_public_path__: string | undefined;
(function setWebpackPublicPath() {
  try {
    // Strategy 1: Check if __CMS_ASSET_BASE__ is already set in the HTML (set by root-config)
    let base: string | undefined = (window as any).__CMS_ASSET_BASE__;
    if (base) {
      console.log('[set-public-path] Using __CMS_ASSET_BASE__ from HTML:', base);
    } else {
      // Strategy 2: Infer from the current script (main.js) URL
      let scriptSrc: string | null = null;
      // Try document.currentScript first (most reliable)
      const current = document.currentScript as HTMLScriptElement | null;
      if (current && current.src) {
        scriptSrc = current.src;
      } else {
        // Fallback: find the script tag that includes 'main' or 'single-spa' in its src
        // Look for the script that loaded this module
        const scripts = Array.from(document.getElementsByTagName('script'));
        for (let i = scripts.length - 1; i >= 0; i--) {
          const src = scripts[i].src;
          if (src && (src.includes('main.js') || src.includes('single-spa') || src.includes('/single-spa/'))) {
            scriptSrc = src;
            break;
          }
        }
      }
      if (scriptSrc) {
        try {
          const url = new URL(scriptSrc, window.location.href);
          // Extract the full path including sub-path (e.g., /single-spa)
          // Get the directory path of the script, not just the origin
          const pathParts = url.pathname.split('/').filter(p => p);
          // Remove the script filename (last part)
          if (pathParts.length > 0 && pathParts[pathParts.length - 1].endsWith('.js')) {
            pathParts.pop();
          }
          // Construct base URL: origin + base path (e.g., https://cms-dev.coherentpixels.com/single-spa)
          if (pathParts.length > 0) {
            base = url.origin + '/' + pathParts.join('/');
          } else {
            base = url.origin;
          }
          console.log('[set-public-path] Inferred base from script URL:', base);
          console.log('[set-public-path] Script URL:', scriptSrc);
        } catch (e) {
          console.warn('[set-public-path] Error parsing script URL:', e);
        }
      }
      // Strategy 3: Final fallback - use window.location
      if (!base) {
        const pathname = window.location.pathname;
        const origin = window.location.origin;
        // Check if we're in a sub-path deployment (e.g., /single-spa/...)
        const pathMatch = pathname.match(/^(\/[^\/]+)/);
        if (pathMatch && (pathname.includes('/single-spa') || pathname.includes('/angular'))) {
          base = origin + pathMatch[1];
          console.log('[set-public-path] Inferred base from location pathname:', base);
        } else {
          base = origin;
          console.log('[set-public-path] Using window.location.origin as fallback:', base);
        }
      }
    }
    if (base) {
      // Remove trailing slash for consistency, then add it for webpack
      base = base.replace(/\/$/, '');
      const webpackPath = base + '/';
      // Set webpack public path - this is critical for chunk loading
      (globalThis as any).__webpack_public_path__ = webpackPath;
      // Also set/update __CMS_ASSET_BASE__ for use in the application
      (window as any).__CMS_ASSET_BASE__ = base;
      console.log('[set-public-path] ✓ Set webpack public path:', (globalThis as any).__webpack_public_path__);
      console.log('[set-public-path] ✓ Set __CMS_ASSET_BASE__:', base);
      try {
        const assetBase = base;
        // Detect if we're in standalone mode (not single-spa)
        const isStandaloneMode = (): boolean => {
          // Check if single-spa is NOT available on window
          if (typeof window !== 'undefined' && !(window as any).singleSpa) {
            // Check if __webpack_public_path__ is not set or is just a simple path (standalone)
              try {
              // Read webpack public path from the global object safely
              const publicPath = typeof (globalThis as any).__webpack_public_path__ !== 'undefined' ? (globalThis as any).__webpack_public_path__ : '';
              // If publicPath is './', '/', or empty, it's likely standalone
              if (!publicPath || publicPath === './' || publicPath === '/' || publicPath === '') {
                return true;
              }
            } catch (e) {
              return true;
            }
          }
          return false;
        };
        const normalizeAssetUrl = (value: string | null | undefined): string | null => {
          if (!value) {
            return null;
          }
          const trimmed = value.trim();
          if (!trimmed) {
            return null;
          }
          // Preserve absolute URLs and data URIs
          if (/^(?:[a-z]+:|\/\/)/i.test(trimmed) || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
            return trimmed;
          }
          const assetIndex = trimmed.indexOf('assets/');
          if (assetIndex === -1) {
            return null;
          }
          const assetPath = trimmed.substring(assetIndex).replace(/^\/+/, '');
          // If in standalone mode, prepend /cms-angular before assets
          if (isStandaloneMode()) {
            return `/cms-angular/${assetPath}`;
          }
          // In single-spa mode, use the assetBase
          const normalizedBase = assetBase.replace(/\/$/, '');
          return `${normalizedBase}/${assetPath}`;
        };
        const rewriteElementAttributes = (element: Element): void => {
          ['src', 'href', 'data-src'].forEach((attr) => {
            if (!element.hasAttribute(attr)) {
              return;
            }
            const current = element.getAttribute(attr);
            const resolved = normalizeAssetUrl(current);
            if (resolved && current !== resolved) {
              element.setAttribute(attr, resolved);
            }
          });
        };
        const processNode = (node: Node): void => {
          if (!(node instanceof Element)) {
            return;
          }
          rewriteElementAttributes(node);
          node.querySelectorAll('[src], [href], [data-src]').forEach((child) => rewriteElementAttributes(child));
        };
        processNode(document.documentElement);
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.target instanceof Element) {
              rewriteElementAttributes(mutation.target);
            }
            mutation.addedNodes.forEach((added) => processNode(added));
          });
        });
        observer.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['src', 'href', 'data-src'],
          childList: true,
          subtree: true
        });
      } catch (assetError) {
        console.warn('[set-public-path] Asset URL normalization failed:', assetError);
      }
    } else {
      console.error('[set-public-path] ✗ Could not determine base path! Chunks may fail to load.');
      // Fallback to relative path
      (globalThis as any).__webpack_public_path__ = './';
      // Even without base, we should still normalize asset URLs for standalone mode
      try {
        const normalizeAssetUrl = (value: string | null | undefined): string | null => {
          if (!value) {
            return null;
          }
          const trimmed = value.trim();
          if (!trimmed) {
            return null;
          }
          // Preserve absolute URLs and data URIs
          if (/^(?:[a-z]+:|\/\/)/i.test(trimmed) || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
            return trimmed;
          }
          const assetIndex = trimmed.indexOf('assets/');
          if (assetIndex === -1) {
            return null;
          }
          const assetPath = trimmed.substring(assetIndex).replace(/^\/+/, '');
          // In standalone mode (no base), prepend /cms-angular
          return `/cms-angular/${assetPath}`;
        };
        const rewriteElementAttributes = (element: Element): void => {
          ['src', 'href', 'data-src'].forEach((attr) => {
            if (!element.hasAttribute(attr)) {
              return;
            }
            const current = element.getAttribute(attr);
            const resolved = normalizeAssetUrl(current);
            if (resolved && current !== resolved) {
              element.setAttribute(attr, resolved);
            }
          });
        };
        const processNode = (node: Node): void => {
          if (!(node instanceof Element)) {
            return;
          }
          rewriteElementAttributes(node);
          node.querySelectorAll('[src], [href], [data-src]').forEach((child) => rewriteElementAttributes(child));
        };
        processNode(document.documentElement);
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.target instanceof Element) {
              rewriteElementAttributes(mutation.target);
            }
            mutation.addedNodes.forEach((added) => processNode(added));
          });
        });
        observer.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['src', 'href', 'data-src'],
          childList: true,
          subtree: true
        });
      } catch (assetError) {
        console.warn('[set-public-path] Asset URL normalization failed:', assetError);
      }
    }
  } catch (error) {
    console.error('[set-public-path] ✗ Error setting public path:', error);
    // Critical: set a fallback to prevent chunk loading failures
    __webpack_public_path__ = './';
  }
})();